// 038 — Trabajo Social: datos y `Par[]` del PDF firmado, verificados contra el papel (PDF oficial, págs. 170-175:
// 170 hoja, 171 evolución, 172-175 instructivo). El componente (`Formulario038.tsx`) solo pinta; todo lo que sale
// impreso se arma acá para poder probarlo sin DOM.
//
// Bloque A ampliado: los datos del establecimiento y del usuario (institución, unicódigo, establecimiento, HCU,
// archivo, apellidos, nombres, sexo, edad y condición de la edad) los imprime el encabezado MSP; acá van los que el
// encabezado NO trae: estado civil, autoidentificación étnica, nacionalidad étnica, pueblos, nivel y estado de
// educación, tipo de empresa, ocupación, seguro, bono, lugar de trabajo, dirección, teléfono, ingresos, nacionalidad
// (instructivo: "ecuatoriana o extranjera") y las dos residencias (usuario y representante, 9 campos c/u).
//
// Los catálogos salen de `paciente/filiacionDatos.ts` (la "LISTA DE CATEGORÍAS" del papel), no se reinventan.
import { AUTOIDENTIFICACION_ETNICA, ESTADOS_CIVILES, ESTADOS_EDUCACION, NACIONALIDADES_ETNICAS, NIVELES_EDUCACION, NACIONALIDADES, PUEBLOS, TIPOS_BONO, TIPOS_EMPRESA, telefonosDesdePatient, type OpcionCatalogo } from '../../paciente/filiacionDatos';
import { paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import type { Patient } from '@medplum/fhirtypes';
import {
  CODIGO_REPRESENTANTE_LEGAL,
  EXT_AUTOIDENTIFICACION_ETNICA,
  EXT_ESTADO_EDUCACION,
  EXT_NACIONALIDAD,
  EXT_NACIONALIDAD_ETNICA,
  EXT_NIVEL_EDUCACION,
  EXT_OCUPACION,
  EXT_PUEBLO,
  EXT_SEGURO_SALUD,
  EXT_TIPO_BONO,
  EXT_TIPO_CONTACTO_PACIENTE,
  EXT_TIPO_EMPRESA,
} from '../../paciente/filiacionCompleta';
import { valorExtensionCoding, valorExtensionTexto } from '../../lib/extensionesFhir';
import type { EgresosFamiliares, SituacionEconomica } from './situacionEconomica';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

export const TOPE_FILAS_038 = 10;

const aOpciones = (catalogo: readonly OpcionCatalogo[]): OpcionCampo[] => catalogo.map((o) => ({ valor: o.codigo, etiqueta: o.etiqueta }));

// ---------- Bloque A ampliado ----------

/** Paper: SOL / CAS / DIV / VIU / UN / U-H / NA (las mismas 7 categorías del catálogo de filiación). */
export const ESTADOS_CIVILES_038: readonly OpcionCampo[] = aOpciones(ESTADOS_CIVILES);

/** Paper: "Nacionalidad" (instructivo, pág. 173): ecuatoriana o extranjera. */
export const NACIONALIDAD_038: readonly OpcionCampo[] = [
  { valor: 'ecuatoriana', etiqueta: 'Ecuatoriana' },
  { valor: 'extranjera', etiqueta: 'Extranjera' },
];

/** Paper: SEGURO SALUD PRINCIPAL = IESS-G, IESS-C, ISSPOL, ISSFA, PRIV., NING. */
export const SEGURO_SALUD_038: readonly OpcionCampo[] = [
  { valor: 'IESS-G', etiqueta: 'IESS-G' },
  { valor: 'IESS-C', etiqueta: 'IESS-C' },
  { valor: 'ISSPOL', etiqueta: 'ISSPOL' },
  { valor: 'ISSFA', etiqueta: 'ISSFA' },
  { valor: 'PRIV', etiqueta: 'Privado (PRIV.)' },
  { valor: 'NING', etiqueta: 'Ninguno (NING.)' },
];

export const CAMPOS_A_038: readonly CampoSimple[] = [
  { clave: 'estadoCivil', etiqueta: 'Estado civil', tipo: 'select', opciones: ESTADOS_CIVILES_038 },
  { clave: 'autoidentificacionEtnica', etiqueta: 'Autoidentificación étnica', tipo: 'select', opciones: aOpciones(AUTOIDENTIFICACION_ETNICA) },
  { clave: 'nacionalidad', etiqueta: 'Nacionalidad', tipo: 'select', opciones: NACIONALIDAD_038 },
  { clave: 'nacionalidadEtnica', etiqueta: 'Nacionalidad étnica', tipo: 'select', opciones: aOpciones(NACIONALIDADES_ETNICAS) },
  { clave: 'pueblos', etiqueta: 'Pueblos', tipo: 'select', opciones: aOpciones(PUEBLOS) },
  { clave: 'nivelEducacion', etiqueta: 'Nivel de educación', tipo: 'select', opciones: aOpciones(NIVELES_EDUCACION) },
  { clave: 'estadoNivelEducacion', etiqueta: 'Estado de nivel de educación', tipo: 'select', opciones: aOpciones(ESTADOS_EDUCACION) },
  { clave: 'tipoEmpresa', etiqueta: 'Tipo de empresa de trabajo', tipo: 'select', opciones: aOpciones(TIPOS_EMPRESA) },
  { clave: 'ocupacion', etiqueta: 'Ocupación / profesión' },
  { clave: 'seguroSalud', etiqueta: 'Seguro de salud principal', tipo: 'select', opciones: SEGURO_SALUD_038 },
  { clave: 'tipoBono', etiqueta: 'Tipo de bono que recibe', tipo: 'select', opciones: aOpciones(TIPOS_BONO) },
  { clave: 'lugarTrabajo', etiqueta: 'Lugar de trabajo' },
  { clave: 'direccionTrabajo', etiqueta: 'Dirección' },
  { clave: 'telefonoTrabajo', etiqueta: 'Teléfono' },
  { clave: 'ingresos', etiqueta: 'Ingresos económicos', tipo: 'numero' },
];

/** Los 9 campos de "DATOS DE RESIDENCIA" (idénticos para el usuario/paciente y para el representante). */
export const CAMPOS_RESIDENCIA_038: readonly CampoSimple[] = [
  { clave: 'provincia', etiqueta: 'Provincia' },
  { clave: 'canton', etiqueta: 'Cantón' },
  { clave: 'parroquia', etiqueta: 'Parroquia' },
  { clave: 'barrioSector', etiqueta: 'Barrio o sector' },
  { clave: 'telefono', etiqueta: 'No. teléfono' },
  { clave: 'correo', etiqueta: 'Correo electrónico' },
  { clave: 'callePrincipal', etiqueta: 'Calle principal' },
  { clave: 'calleSecundaria', etiqueta: 'Calle secundaria' },
  { clave: 'referenciaDomiciliaria', etiqueta: 'Referencia domiciliaria' },
];

export const valoresVaciosA038 = (): Record<string, string> => valoresVacios(CAMPOS_A_038);
export const valoresVaciosResidencia038 = (): Record<string, string> => valoresVacios(CAMPOS_RESIDENCIA_038);

// ---------- Precarga desde el Patient ----------

/** La ficha guarda el seguro con los mismos valores del papel (IESS-G, PRIV, NING...): se copian tal cual. Las claves del 008 (`iessG`...) se toleran como respaldo. */
const SEGURO_ALTERNO_038: Readonly<Record<string, string>> = { iessG: 'IESS-G', iessC: 'IESS-C', isspol: 'ISSPOL', issfa: 'ISSFA', privado: 'PRIV', ninguno: 'NING' };

export interface PrecargaPatient038 {
  readonly datosA: Valores;
  readonly residenciaUsuario: Valores;
  readonly nombreRepresentante: string;
  readonly residenciaRepresentante: Valores;
}

/**
 * Datos del bloque A que el encabezado MSP no imprime y que ya viven en el `Patient` (ficha de datos demográficos): se precargan y
 * quedan editables. Un valor que no es una opción del papel NO se copia (nada se adivina); lo que el `Patient` no tiene (ingresos, lugar
 * de trabajo, dirección/teléfono del trabajo, residencia del representante salvo su teléfono) queda vacío.
 */
export function precargaDesdePatient038(patient: Patient | undefined): PrecargaPatient038 {
  const datosA: Record<string, string> = valoresVaciosA038();
  const residenciaUsuario: Record<string, string> = valoresVaciosResidencia038();
  const residenciaRepresentante: Record<string, string> = valoresVaciosResidencia038();
  if (!patient) return { datosA, residenciaUsuario, nombreRepresentante: '', residenciaRepresentante };
  const ext = patient.extension;
  const poner = (clave: string, valor: string, opciones?: readonly OpcionCampo[]): void => {
    const v = valor.trim();
    if (!v || (opciones && !opciones.some((o) => o.valor === v))) return;
    datosA[clave] = v;
  };
  const opcionesDe = (clave: string): readonly OpcionCampo[] | undefined => CAMPOS_A_038.find((c) => c.clave === clave)?.opciones;
  poner('estadoCivil', patient.maritalStatus?.text ?? '', opcionesDe('estadoCivil'));
  poner('autoidentificacionEtnica', valorExtensionCoding(ext, EXT_AUTOIDENTIFICACION_ETNICA)?.code ?? '', opcionesDe('autoidentificacionEtnica'));
  const nacionalidad = valorExtensionTexto(ext, EXT_NACIONALIDAD).trim();
  // Solo si es una nacionalidad del catálogo de la ficha (o una variante de "ecuatoriana"); un valor viejo fuera del catálogo no se adivina.
  if (nacionalidad.toLowerCase().startsWith('ecuator')) datosA.nacionalidad = 'ecuatoriana';
  else if (NACIONALIDADES.some((n) => n.codigo === nacionalidad)) datosA.nacionalidad = 'extranjera';
  poner('nacionalidadEtnica', valorExtensionTexto(ext, EXT_NACIONALIDAD_ETNICA), opcionesDe('nacionalidadEtnica'));
  poner('pueblos', valorExtensionTexto(ext, EXT_PUEBLO), opcionesDe('pueblos'));
  poner('nivelEducacion', valorExtensionTexto(ext, EXT_NIVEL_EDUCACION), opcionesDe('nivelEducacion'));
  poner('estadoNivelEducacion', valorExtensionTexto(ext, EXT_ESTADO_EDUCACION), opcionesDe('estadoNivelEducacion'));
  poner('tipoEmpresa', valorExtensionTexto(ext, EXT_TIPO_EMPRESA), opcionesDe('tipoEmpresa'));
  poner('ocupacion', valorExtensionTexto(ext, EXT_OCUPACION));
  const seguro = valorExtensionTexto(ext, EXT_SEGURO_SALUD);
  poner('seguroSalud', SEGURO_ALTERNO_038[seguro] ?? seguro, opcionesDe('seguroSalud'));
  poner('tipoBono', valorExtensionTexto(ext, EXT_TIPO_BONO), opcionesDe('tipoBono'));

  const dir = patient.address?.[0];
  const tel = telefonosDesdePatient(patient);
  Object.assign(residenciaUsuario, {
    provincia: dir?.state ?? '',
    canton: dir?.district ?? '',
    parroquia: dir?.city ?? '',
    barrioSector: dir?.line?.[0] ?? '',
    callePrincipal: dir?.line?.[1] ?? '',
    calleSecundaria: dir?.line?.[2] ?? '',
    referenciaDomiciliaria: dir?.line?.[3] ?? '',
    telefono: tel.celular || tel.fijo,
    correo: patient.telecom?.find((c) => c.system === 'email')?.value ?? '',
  });
  const representante = patient.contact?.find((c) => c.extension?.some((e) => e.url === EXT_TIPO_CONTACTO_PACIENTE && e.valueCode === CODIGO_REPRESENTANTE_LEGAL));
  residenciaRepresentante.telefono = representante?.telecom?.find((c) => c.system === 'phone')?.value ?? '';
  return { datosA, residenciaUsuario, nombreRepresentante: representante?.name?.text ?? '', residenciaRepresentante };
}

// ---------- Grupos y condiciones (orden del papel) ----------

export const GRUPOS_PRIORITARIOS_038 = ['ninos', 'adolescentes', 'embarazadas', 'adultoMayor', 'discapacidad', 'enfermedadesRarasCatastroficas', 'ppl'] as const;

/**
 * Orden del papel, fila 1 y fila 2 de "CONDICIONES DE RIESGO SOCIO ECONÓMICO". La violencia son TRES casillas separadas
 * (presunta violencia física / psicológica / sexual); antes era una sola.
 */
export const CONDICIONES_RIESGO_038 = [
  'ninosRiesgoSocial',
  'adolescenteRiesgo',
  'embarazoRiesgoSocial',
  'violenciaFisica',
  'violenciaPsicologica',
  'violenciaSexual',
  'trastornosPsicosociales',
  'adicciones',
  'riesgoAccidentes',
  'pets',
  'delincuencia',
  'desempleo',
  'pobreza',
  'bajaEscolaridad',
  'hacinamiento',
  'otro',
] as const;

// ---------- Vivienda ----------

export const SERVICIOS_BASICOS_038 = ['luz', 'aguaPotable', 'recoleccionBasura', 'alcantarillado', 'telefono', 'internet'] as const;
export const RIESGOS_AMBIENTALES_038 = ['insalubreMarginalInsegura', 'animalesIntradomicilio', 'contaminacion', 'malManejoDesechos', 'letrinas'] as const;
export const TENENCIA_OPCIONES_038 = ['propia', 'arrendada', 'por-servicio', 'anticresis', 'hipoteca', 'prestada', 'albergue', 'hogar-proteccion', 'otro'] as const;
export const TIPO_VIVIENDA_OPCIONES_038 = ['casa', 'villa', 'cuarto-inquilinato', 'suite', 'departamento', 'mediagua', 'rancho', 'covacha-choza', 'otro'] as const;
export const MATERIAL_OPCIONES_038 = ['hormigon', 'bloque', 'ladrillo', 'adobe', 'tapia', 'madera', 'cana-no-revestida', 'cana-revestida', 'mixta', 'otro'] as const;

export const TIPO_INTERVENCION_OPCIONES_038 = [
  'entrevista',
  'visita-domiciliaria',
  'gestiones-internas',
  'gestiones-externas',
  'prestacion-servicios',
  'referencias-especialidad',
  'dictamen-discapacidad',
  'consejeria-vih',
  'escalas-ciclo-vida',
] as const;

export interface EstadoVivienda038 {
  readonly tenencia: string;
  readonly tipo: string;
  readonly material: string;
  readonly numeroCuartos: number | '';
  readonly numeroBanos: number | '';
  readonly personasPorDormitorio: number | '';
  readonly numeroCamas: number | '';
  readonly serviciosBasicos: Readonly<Record<string, boolean>>;
  readonly riesgosAmbientales: Readonly<Record<string, boolean>>;
}

export const VIVIENDA_VACIA_038: EstadoVivienda038 = {
  tenencia: '',
  tipo: '',
  material: '',
  numeroCuartos: '',
  numeroBanos: '',
  personasPorDormitorio: '',
  numeroCamas: '',
  serviciosBasicos: {},
  riesgosAmbientales: {},
};

// ---------- Estructura familiar y evolución ----------

export interface FamiliaFila038 {
  nombres: string;
  sexo: string;
  edad: string;
  estadoCivil: string;
  relacion: string;
  nivelEducacion: string;
  profesion: string;
  ocupacion: string;
  ingresoEconomico: string;
  seguroSalud: string;
  condicionesRiesgo: string;
}
export const FAMILIA_VACIA_038: FamiliaFila038 = { nombres: '', sexo: '', edad: '', estadoCivil: '', relacion: '', nivelEducacion: '', profesion: '', ocupacion: '', ingresoEconomico: '', seguroSalud: '', condicionesRiesgo: '' };

/**
 * "TRABAJADORA SOCIAL" en el papel trae dos casillas por entrada (CÓDIGO y FIRMA): ya NO se teclean. Quien interviene es quien firma el
 * documento, y su nombre, su cédula y la fecha los arma el servidor en el bloque F (`CODIGOS_CON_CEDULA_PROFESIONAL`); un nombre o código
 * tecleado aquí podría ser el de otra persona. DECISIÓN (firmante único): un documento 038 lo firma UNA sola persona, así que todas las
 * filas de evolución quedan atribuidas a ese firmante; si otra trabajadora social interviene, registra su propio documento firmado por ella. Un estado viejo con `codigo`/`firma` se lee sin error y ya no se vuelve a imprimir.
 */
export interface EvolucionFila038 {
  fecha: string;
  hora: string;
  tipoIntervencion: string;
  problemasIdentificados: string;
  planIntervencion: string;
}
export const EVOLUCION_VACIA_038: EvolucionFila038 = { fecha: '', hora: '', tipoIntervencion: '', problemasIdentificados: '', planIntervencion: '' };

export interface EstadoFormulario038 {
  readonly motivoIntervencion: string;
  readonly planSeguimiento: string;
  readonly datosA: Valores;
  readonly residenciaUsuario: Valores;
  readonly nombreRepresentante: string;
  readonly residenciaRepresentante: Valores;
  readonly gruposPrioritarios: Readonly<Record<string, boolean>>;
  readonly condicionesRiesgo: Readonly<Record<string, boolean>>;
  readonly familia: readonly FamiliaFila038[];
  readonly ingresos: number | '';
  readonly egresos: EgresosFamiliares;
  readonly situacionEconomica: SituacionEconomica;
  readonly vivienda: EstadoVivienda038;
  readonly evolucion: readonly EvolucionFila038[];
}

// ---------- Pares del PDF firmado ----------

const marcadas = (t: Traducir, claves: readonly string[], valores: Readonly<Record<string, boolean>>, prefijoI18n: string): string =>
  claves.filter((c) => valores[c]).map((c) => t(`${prefijoI18n}.${c}`)).join(', ') || '—';

const numero = (n: number | ''): string => (n === '' ? '' : String(n));

const rotulada = (t: Traducir, prefijoI18n: string, campos: readonly string[], valores: Readonly<Record<string, string>>): string =>
  campos
    .map((c) => ({ etiqueta: t(`${prefijoI18n}.${c}`), valor: (valores[c] ?? '').trim() }))
    .filter((c) => c.valor !== '')
    .map((c) => `${c.etiqueta}: ${c.valor}`)
    .join('; ');

const CLAVES_FAMILIA = Object.keys(FAMILIA_VACIA_038) as (keyof FamiliaFila038)[];

/**
 * Todo lo del formulario salvo los diagnósticos sociales (esos crean `Condition` y los agrega el componente). Los `Par`
 * con respuesta vacía se descartan al final. Cada entrada de estructura familiar / evolución sale con el rótulo de su
 * columna (antes salía "a | b | c" sin rótulos, ilegible), y los códigos internos de vivienda salen como rótulos.
 */
export function paresFormulario038(t: Traducir, e: EstadoFormulario038): Par[] {
  const v = e.vivienda;
  const rotuloOpcion = (prefijo: string, clave: string): string => (clave ? t(`${prefijo}.${clave}`) : '—');
  const pares: Par[] = [
    ...paresDeCampos(CAMPOS_A_038, e.datosA),
    { pregunta: t('formularios.f038.seccionGruposPrioritarios'), respuesta: marcadas(t, GRUPOS_PRIORITARIOS_038, e.gruposPrioritarios, 'formularios.f038.gruposPrioritarios') },
    { pregunta: t('formularios.f038.seccionCondicionesRiesgo'), respuesta: marcadas(t, CONDICIONES_RIESGO_038, e.condicionesRiesgo, 'formularios.f038.condicionesRiesgo') },
    { pregunta: t('formularios.f038.motivoIntervencion'), respuesta: e.motivoIntervencion.trim() },
    ...paresDeCampos(CAMPOS_RESIDENCIA_038, e.residenciaUsuario, `${t('formularios.f038.seccionResidenciaUsuario')} — `),
    { pregunta: t('formularios.f038.nombreRepresentante'), respuesta: e.nombreRepresentante.trim() },
    ...paresDeCampos(CAMPOS_RESIDENCIA_038, e.residenciaRepresentante, `${t('formularios.f038.seccionResidenciaRepresentante')} — `),
    ...e.familia.map((f, i) => ({ pregunta: `${t('formularios.f038.seccionEstructuraFamiliar')} #${i + 1}`, respuesta: rotulada(t, 'formularios.f038.estructuraFamiliar', CLAVES_FAMILIA, f as unknown as Record<string, string>) })),
    { pregunta: t('formularios.f038.situacionEconomica.totalIngresos'), respuesta: String(e.ingresos || 0) },
    ...(['alimentacion', 'vivienda', 'serviciosBasicos', 'educacion', 'salud', 'movilizacion', 'otros'] as const).map((rubro) => ({
      pregunta: t(`formularios.f038.situacionEconomica.egreso${rubro.charAt(0).toUpperCase()}${rubro.slice(1)}`),
      respuesta: e.egresos[rubro] ? String(e.egresos[rubro]) : '',
    })),
    { pregunta: t('formularios.f038.situacionEconomica.totalEgresos'), respuesta: String(e.situacionEconomica.totalEgresos) },
    { pregunta: t('formularios.f038.situacionEconomica.disponibilidad'), respuesta: String(e.situacionEconomica.disponibilidad) },
    { pregunta: t('formularios.f038.vivienda.tenencia'), respuesta: rotuloOpcion('formularios.f038.vivienda.tenenciaOpciones', v.tenencia) },
    { pregunta: t('formularios.f038.vivienda.tipo'), respuesta: rotuloOpcion('formularios.f038.vivienda.tipoOpciones', v.tipo) },
    { pregunta: t('formularios.f038.vivienda.numeroCuartos'), respuesta: numero(v.numeroCuartos) },
    { pregunta: t('formularios.f038.vivienda.numeroBanos'), respuesta: numero(v.numeroBanos) },
    { pregunta: t('formularios.f038.vivienda.personasPorDormitorio'), respuesta: numero(v.personasPorDormitorio) },
    { pregunta: t('formularios.f038.vivienda.numeroCamas'), respuesta: numero(v.numeroCamas) },
    { pregunta: t('formularios.f038.vivienda.materialConstructivo'), respuesta: rotuloOpcion('formularios.f038.vivienda.materialOpciones', v.material) },
    { pregunta: t('formularios.f038.vivienda.seccionServiciosBasicos'), respuesta: marcadas(t, SERVICIOS_BASICOS_038, v.serviciosBasicos, 'formularios.f038.vivienda.serviciosBasicos') },
    { pregunta: t('formularios.f038.vivienda.seccionRiesgosAmbientales'), respuesta: marcadas(t, RIESGOS_AMBIENTALES_038, v.riesgosAmbientales, 'formularios.f038.vivienda.riesgosAmbientales') },
    ...e.evolucion.map((f, i) => ({
      pregunta: `${t('formularios.f038.seccionEvolucionCaso')} #${i + 1}`,
      respuesta: [
        f.fecha && `${t('formularios.f038.evolucionCaso.fecha')}: ${f.fecha}`,
        f.hora && `${t('formularios.f038.evolucionCaso.hora')}: ${f.hora}`,
        f.tipoIntervencion && `${t('formularios.f038.evolucionCaso.tipoIntervencion')}: ${t(`formularios.f038.evolucionCaso.tipoIntervencionOpciones.${f.tipoIntervencion}`)}`,
        f.problemasIdentificados.trim() && `${t('formularios.f038.evolucionCaso.problemasIdentificados')}: ${f.problemasIdentificados.trim()}`,
        f.planIntervencion.trim() && `${t('formularios.f038.evolucionCaso.planIntervencion')}: ${f.planIntervencion.trim()}`,
      ]
        .filter(Boolean)
        .join('; '),
    })),
    { pregunta: t('formularios.f038.planSeguimiento'), respuesta: e.planSeguimiento.trim() },
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
